/* ============================================================
 * Jangela debug — suppression manager (R166)
 * ============================================================
 * The Force Raffle Winner UI was consolidated into the top-right
 * gear panel (see _godBuildPanel in hoh-fresh-faces.js). This file
 * is now JUST the suppression layer: when Jangela picks a forced
 * winner, every debug surface hides until the roll completes + a
 * 60-second grace window timed from the winner reveal. The grace gives
 * screen recordings / screenshots a clean reveal of the winner before
 * the debug UI comes back.
 *
 * State machine:
 *   NORMAL       no forced winner OR god mode off → debug visible
 *   FORCED       user just set a forced winner    → debug hidden
 *   GRACE        forced winner was consumed by    → debug hidden
 *                a roll, 60s grace window         → until grace ends
 *
 * Transitions:
 *   NORMAL → FORCED   on  hoh:forced-winner-changed { charId: non-null }
 *   FORCED → GRACE    on  hoh:forced-winner-consumed (fired by picker)
 *   FORCED → NORMAL   on  hoh:forced-winner-changed { charId: null }
 *                     when NOT preceded by a consumed event (i.e., user
 *                     clicked Clear in the gear panel)
 *   GRACE  → NORMAL   on  grace timer expiry (60s after consume)
 *   any    → NORMAL   on  hoh:god-mode-changed { on: false }
 *
 * Safety net: a 5-minute fallback timer auto-restores debug UI if the
 * user sets a forced winner and then never rolls. Without this, an
 * accidental selection could trap them with hidden UI indefinitely.
 *
 * What gets suppressed: anything visually marked as debug-only via the
 * `body.hoh-debug-suppressed <selector> { display:none }` rules in
 * hall-of-heroes.css. Currently:
 *   - #hoh-god-debug-panel       (the gear + its panel)
 *   - #hoh-god-mode-badge        (no longer mounted, but safe-listed)
 *   - #hoh-jangela-pill          (no longer mounted, but safe-listed)
 * ============================================================ */
(function(){
  'use strict';

  var GRACE_MS              = 60000;        /* 60s after consume → restore */
  var SAFETY_TIMEOUT_MS     = 5 * 60 * 1000;/* 5 min idle → restore       */
  var SUPPRESS_CLASS        = 'hoh-debug-suppressed';

  var graceTimerId    = 0;
  var safetyTimerId   = 0;
  /* Distinguishes consumed-by-roll from cleared-by-user. The picker
     fires hoh:forced-winner-consumed BEFORE hohClearForcedWinner; this
     flag is set there and read by the subsequent
     hoh:forced-winner-changed { charId: null } handler so we know
     whether to start the grace timer or restore immediately. */
  var pendingConsume  = false;

  function godMode(){
    try { return typeof window.hohIsGodMode === 'function' && window.hohIsGodMode(); }
    catch(_){ return false; }
  }

  function setSuppressed(on){
    try {
      document.body.classList.toggle(SUPPRESS_CLASS, !!on);
    } catch(_){}
  }

  function startGraceTimer(){
    clearGraceTimer();
    graceTimerId = setTimeout(function(){
      graceTimerId = 0;
      /* If god mode dropped during grace, suppression already gone. */
      setSuppressed(false);
      clearSafetyTimer();
    }, GRACE_MS);
  }
  function clearGraceTimer(){
    if(graceTimerId){ clearTimeout(graceTimerId); graceTimerId = 0; }
  }
  function startSafetyTimer(){
    clearSafetyTimer();
    safetyTimerId = setTimeout(function(){
      safetyTimerId = 0;
      /* Idle safety: user set a forced winner and never rolled. Restore
         debug UI and ALSO clear the override so the next roll isn't
         silently captured later. The user can re-set if they meant it. */
      try {
        if(typeof window.hohClearForcedWinner === 'function') window.hohClearForcedWinner();
      } catch(_){}
      setSuppressed(false);
    }, SAFETY_TIMEOUT_MS);
  }
  function clearSafetyTimer(){
    if(safetyTimerId){ clearTimeout(safetyTimerId); safetyTimerId = 0; }
  }

  /* Forced-winner state transitions. */
  document.addEventListener('hoh:forced-winner-changed', function(ev){
    var charId = ev && ev.detail && ev.detail.charId;
    if(!godMode()){
      /* god-mode-off handler will run too; do nothing here to avoid
         races. */
      return;
    }
    if(charId){
      /* NORMAL/GRACE → FORCED. Hide debug UI immediately. Clear any
         in-flight grace timer (a fresh forced selection overrides). */
      clearGraceTimer();
      startSafetyTimer();
      setSuppressed(true);
      return;
    }
    /* charId === null → either consumed-by-roll or cleared-by-user. */
    if(pendingConsume){
      pendingConsume = false;
      /* FORCED → GRACE. Keep debug hidden for 60s so the winner card
         + confetti reveal without UI clutter. */
      clearSafetyTimer();
      startGraceTimer();
    } else {
      /* User clicked Clear before rolling. FORCED → NORMAL. */
      clearGraceTimer();
      clearSafetyTimer();
      setSuppressed(false);
    }
  });

  /* The picker fires this BEFORE calling hohClearForcedWinner during
     a roll consumption. We set a one-shot flag so the subsequent
     hoh:forced-winner-changed { charId: null } enters GRACE instead
     of NORMAL. The flag auto-clears in the handler above. */
  document.addEventListener('hoh:forced-winner-consumed', function(){
    pendingConsume = true;
  });

  /* R229: the picker consumes the forced winner at roll START, which
     begins the grace timer right away. The reveal animation then runs for
     a few seconds. The user wants the gear UI to stay hidden for a full
     minute AFTER the winner actually lands, so when the reveal fires and
     we are already in the consume grace, restart the 60s window from here.
     Guarded on an active grace timer, so ordinary random rolls (no
     suppression running) are a no-op. */
  document.addEventListener('hoh:forced-winner-revealed', function(){
    if(graceTimerId){
      startGraceTimer();
    }
  });

  /* God mode going off purges everything. */
  document.addEventListener('hoh:god-mode-changed', function(ev){
    var on = ev && ev.detail && !!ev.detail.on;
    if(!on){
      pendingConsume = false;
      clearGraceTimer();
      clearSafetyTimer();
      setSuppressed(false);
    }
  });
})();
